import React, { useEffect, useState } from "react";
import { classnames, Icon } from "@culturehq/components";

import { Trans } from "app/utils";

import LazyInputRange from "components/survey/item-types/LazyInputRange";
import { CurrentOption } from "./EffectsSDKCameraRecorder";
import EffectsStarsIcon from "../icons/EffectsStarsIcon";
import effectsItem from "../effects-items.json";
import HDIcon from "../icons/HDIcon";
import PaintBrushIcon from "../icons/PaintBrushIcon";
import BucketIcon from "../icons/BucketIcon";
import SunIcon from "../icons/SunIcon";
import ZoomInIcon from "../icons/ZoomInIcon";

interface EffectOptionsProps {
  currentOption: CurrentOption;
  effectsSDK: any;
  isDisableOption: (option: CurrentOption) => boolean;
  onSelectOption: (option: CurrentOption) => void;
  open: boolean;
}

const EffectIcon: React.FC<{ effectName: string }> = ({ effectName }) => {
  switch (effectName) {
    case "Video Quality":
      return <HDIcon className="icon" />;
    case "Retouch":
      return <PaintBrushIcon className="icon" />;
    case "Color Filter":
      return <BucketIcon className="icon" />;
    case "Low Light":
      return <SunIcon className="icon" />;
    default:
      return <ZoomInIcon className="icon" />;
  }
};

const EffectOptions: React.FC<EffectOptionsProps> = ({
  currentOption,
  effectsSDK,
  isDisableOption,
  onSelectOption,
  open
}) => {
  const [selectedEffects, setSelectedEffects] = useState< number[]>([]);
  const [videoQuality, setVideoQuality] = useState(0);
  const [retouch, setRetouch] = useState(0);
  const [colorFilter, setColorFilter] = useState(0);
  const [lowLight, setLowLight] = useState(0);
  const [smartZoom, setSmartZoom] = useState(0);

  const effectsStates = [
    videoQuality,
    retouch,
    colorFilter,
    lowLight,
    smartZoom
  ];

  const effectsSetters = [
    setVideoQuality,
    setRetouch,
    setColorFilter,
    setLowLight,
    setSmartZoom
  ];

  useEffect(() => {
    if (!open) {
      setSelectedEffects([]);
      setVideoQuality(0);
      setRetouch(0);
      setColorFilter(0);
      setLowLight(0);
      setSmartZoom(0);
    }
  }, [open]);

  const isActiveEffect = (index: number) => selectedEffects.find(se => se === index);

  const manageEffect = async (index: number, enable: boolean, value?: number) => {
    switch (index) {
      // Video Quality
      case 0:
        if (effectsSDK) {
          if (enable) {
            effectsSDK.setSharpnessEffectConfig({ power: value ? value / 100 : 0.5 });
            effectsSDK.enableSharpnessEffect();
          } else {
            effectsSDK.disableSharpnessEffect();
          }
        }
        break;
      // Retouch
      case 1:
        if (enable) {
          effectsSDK.enableBeautification();
          effectsSDK.setBeautificationLevel(value ? value / 100 : 0.5);
        } else {
          effectsSDK.disableBeautification();
        }
        break;
      case 2:
        // Color Filter
        if (effectsSDK) {
          if (enable) {
            effectsSDK.enableColorCorrector();
            effectsSDK.setColorCorrectorPower(value ? value / 100 : 0.5);
          } else {
            effectsSDK.disableColorCorrector();
          }
        }
        break;
      // Low
      case 3:
        if (effectsSDK) {
          if (enable) {
            effectsSDK.enableLowLightEffect();
            effectsSDK.setLowLightEffectPower(value ? value / 100 : 0.5);
          } else {
            effectsSDK.disableLowLightEffect();
          }
        }
        break;
      // Smart Zoom
      case 4:
        if (effectsSDK) {
          if (enable) {
            effectsSDK.enableSmartZoom();
            effectsSDK.setFaceArea(value ? value / 100 : 0.5);
          } else {
            effectsSDK.disableSmartZoom();
          }
        }
        break;
      default:
        break;
    }
  };

  const handleSelectEffect = (item: any, index: number) => {
    const existingEffect = selectedEffects.findIndex(se => se === index);
    if (existingEffect !== -1) {
      // removing effect
      setSelectedEffects(selectedEffects.filter(se => se !== index));
      effectsSetters[index](0);
      manageEffect(index, false);
    } else {
      // adding effect
      setSelectedEffects([...selectedEffects, index]);
      effectsSetters[index](item.defaultValue);
      manageEffect(index, true, item.defaultValue);
    }
  };

  const handleRangeEffectChange = (index: number, range: number) => {
    const isActive = isActiveEffect(index) !== undefined;
    if (range === 0 && isActive) {
      setSelectedEffects(selectedEffects.filter(se => se !== index));
      manageEffect(index, false);
    }
    if (range !== 0 && !isActive) {
      setSelectedEffects([...selectedEffects, index]);
      manageEffect(index, true, range);
    }

    if (range !== 0 && isActive) {
      manageEffect(index, true, range);
    }
    effectsSetters[index](range);
  };

  return (
    <div
      data-testid="effect-options"
      className={classnames("Options-Container__effects", isDisableOption("effects") ? "disabled" : "")}
    >
      {currentOption === "effects" && (
        <div
          data-testid="effect-options-menu"
          className="option-menu option-menu__effects chq-ffd--sl--opts chq-ffd--sl--opts-open"
        >
          <div className="items-container">
            {effectsItem.effects.map((item, index) => (
              <div
                key={index}
                data-testid={`effect-item-${index}`}
                className="effects-item"
              >
                <div
                  data-testid={`effect-name-${index}`}
                  onClick={() => handleSelectEffect(item, index)}
                >
                  <Trans>{item.name}</Trans>
                </div>
                <div
                  className={isActiveEffect(index) !== undefined ? "icon-container__active" : "icon-container"}
                  onClick={() => handleSelectEffect(item, index)}
                  data-testid={`effect-${index}-selected`}
                  data-selected={isActiveEffect(index) !== undefined}
                >
                  <EffectIcon effectName={item.name} />
                </div>
                <div className="range-bar">
                  <LazyInputRange
                    data-testid={`effect-range-${index}`}
                    maxValue={100}
                    minValue={0}
                    value={effectsStates[index]}
                    onChange={(range: any) => {
                      handleRangeEffectChange(index, range);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div
        data-testid="effect-option-button"
        className="option"
        onClick={() => onSelectOption("effects")}
      >
        <div className="icon-container">
          {currentOption === "effects" ? (
            <Icon className="icon-container__icon" icon="close" />
          ) : <EffectsStarsIcon className="icon-container__icon" />}
        </div>
        <span className="option-legend"><Trans>Effects</Trans></span>
      </div>
      <div
        data-testid="effect-option-arrow"
        className="arrow"
        onClick={() => onSelectOption("effects")}
      >
        <span aria-hidden="true" className={classnames("chq-dd--caret video-effects", { "chq-dd--caret-ex ": currentOption === "effects" })} />
      </div>
    </div>
  );
};

export default EffectOptions;
